import { useState, useEffect, useRef } from "react";
import mqtt, { IClientOptions, MqttClient } from "mqtt";

export interface DriverLocation {
  lat: number;
  lng: number;
  driver_status: string;
  battery: number;
  speed: number;
  heading: number;
  orderIds: string[];
}

interface UseDriverLocationProps {
  selectedBranchId: string;
  selectedBrandId: string;
  isMapLoaded: boolean;
  brandsLength: number;
}

export const useDriverLocation = ({
  selectedBranchId,
  selectedBrandId,
  isMapLoaded,
  brandsLength,
}: UseDriverLocationProps) => {
  const [realTimeDrivers, setRealTimeDrivers] = useState<{
    [key: string]: DriverLocation;
  }>({});
  const [mqttConnected, setMqttConnected] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [mqttClient, setMqttClient] = useState<MqttClient | null>(null);

  // useRef to persist client instance between re-renders and for cleanup
  const mqttClientRef = useRef<MqttClient | null>(null);

  useEffect(() => {
    // Only connect if map is loaded, branch is selected, and brands are loaded
    if (!isMapLoaded || !selectedBranchId || !selectedBrandId || !brandsLength)
      return;

    setMqttConnected(false);
    setLoadingDrivers(true);

    const options: IClientOptions = {
      clientId: `web_${Math.random().toString(16).slice(3)}`,
      clean: true,
      protocol: "wss",
      path: "/mqtt",
      rejectUnauthorized: true,
      keepalive: 60,
      reconnectPeriod: 5000,
      connectTimeout: 30 * 1000, // 30 seconds
      username: import.meta.env.VITE_MQTT_USERNAME,
      password: import.meta.env.VITE_MQTT_PASSWORD,
    };

    const client: MqttClient = mqtt.connect(
      import.meta.env.VITE_MQTT_BROKER_URL,
      options,
    );

    mqttClientRef.current = client;
    setMqttClient(client);

    client.on("connect", () => {
      setMqttConnected(true);
      setLoadingDrivers(false);
      // Subscribe to the branch-specific topic
      client.subscribe(`branch/${selectedBranchId}/location`, (err) => {
        if (err) {
          console.error("Error subscribing to MQTT topic:", err);
        }
      });
    });

    client.on("message", (_, message: Buffer) => {
      try {
        const data = JSON.parse(message.toString());
        setRealTimeDrivers((prev: { [key: string]: DriverLocation }) => ({
          ...prev,
          [data.driverId]: {
            lat: data.latitude,
            lng: data.longitude,
            driver_status: data.driver_status ?? data.status,
            battery: data.driver_battery,
            speed: data.speed,
            heading: data.heading,
            orderIds: data.orderIds,
          },
        }));
      } catch (error) {
        console.error("Error parsing MQTT message:", error);
      }
    });

    client.on("error", (err: Error) => {
      console.error("MQTT connection error:", err);
      setMqttConnected(false);
      setLoadingDrivers(false);
    });

    client.on("close", () => {
      setMqttConnected(false);
      setLoadingDrivers(false);
    });

    client.on("reconnect", () => {
      setMqttConnected(false);
      setLoadingDrivers(true);
    });

    client.on("offline", () => {
      setMqttConnected(false);
      setLoadingDrivers(false);
    });

    // Cleanup on unmount or when branch changes
    return () => {
      // Only call end if client exists and is not already closed or closing
      if (mqttClientRef.current) {
        try {
          // client.end() is safe to call regardless of state, but we avoid race conditions.
          mqttClientRef.current.end(true, () => {
            mqttClientRef.current = null;
          });
        } catch (e) {
          // Silently ignore
        }
      }
      setMqttConnected(false);
      setLoadingDrivers(false);
      setMqttClient(null);
    };
  }, [isMapLoaded, selectedBranchId, selectedBrandId, brandsLength]);

  return {
    realTimeDrivers,
    mqttConnected,
    loadingDrivers,
    mqttClient,
  };
};
